/**
 * Vnutri — Sunny Modernism illustration set.
 * Flat shapes, hard light from upper-left, long 45° shadows.
 * Palette:
 *   indigo    #1B2A41  (primary form + deep shadow)
 *   parchment #F4F1EA  (background / negative space)
 *   terracotta #C46342 (accent: sun, highlights)
 *   cream     #E8E2D3  (mid tone)
 */

const INDIGO = "#1B2A41";
const PARCHMENT = "#F4F1EA";
const TERRACOTTA = "#C46342";
const CREAM = "#E8E2D3";
const SHADOW = "#11192A";

/* ---------- Hero: desk scene ---------- */
export const WorkspaceScene = ({ className = "" }) => (
    <svg
        viewBox="0 0 640 640"
        className={className}
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
    >
        {/* Sun */}
        <g className="vnutri-sun-rotate" style={{ transformOrigin: "520px 120px" }}>
            <circle cx="520" cy="120" r="72" fill={TERRACOTTA} />
            {Array.from({ length: 12 }).map((_, i) => {
                const a = (i * Math.PI) / 6;
                const x1 = 520 + Math.cos(a) * 90;
                const y1 = 120 + Math.sin(a) * 90;
                const x2 = 520 + Math.cos(a) * 112;
                const y2 = 120 + Math.sin(a) * 112;
                return (
                    <line
                        key={i}
                        x1={x1}
                        y1={y1}
                        x2={x2}
                        y2={y2}
                        stroke={TERRACOTTA}
                        strokeWidth="6"
                        strokeLinecap="round"
                    />
                );
            })}
        </g>

        {/* Back wall / horizon */}
        <rect x="0" y="0" width="640" height="420" fill={PARCHMENT} />
        <rect x="0" y="420" width="640" height="220" fill={CREAM} />

        {/* Window on the left */}
        <rect x="30" y="80" width="120" height="240" fill={INDIGO} />
        <rect x="44" y="94" width="92" height="212" fill={PARCHMENT} />
        <line x1="90" y1="94" x2="90" y2="306" stroke={INDIGO} strokeWidth="4" />
        <line x1="44" y1="200" x2="136" y2="200" stroke={INDIGO} strokeWidth="4" />

        {/* Long sunbeam across floor */}
        <polygon
            points="150,320 300,320 520,640 210,640"
            fill={TERRACOTTA}
            opacity="0.18"
        />

        {/* Desk */}
        <rect x="80" y="400" width="500" height="28" fill={INDIGO} />
        {/* Desk front shadow */}
        <polygon points="80,428 580,428 610,460 110,460" fill={SHADOW} />
        {/* Desk legs */}
        <rect x="120" y="428" width="18" height="150" fill={INDIGO} />
        <rect x="522" y="428" width="18" height="150" fill={INDIGO} />
        {/* Leg long shadows */}
        <polygon points="138,578 168,608 150,608 120,578" fill={SHADOW} opacity="0.3" />
        <polygon points="540,578 570,608 552,608 522,578" fill={SHADOW} opacity="0.3" />

        {/* Laptop — shadow first */}
        <polygon
            points="210,400 420,400 470,440 260,440"
            fill={SHADOW}
            opacity="0.35"
        />
        {/* Laptop base */}
        <rect x="210" y="380" width="210" height="20" fill={CREAM} />
        <rect x="210" y="380" width="210" height="6" fill={INDIGO} />
        {/* Laptop screen */}
        <rect x="222" y="250" width="186" height="130" fill={INDIGO} />
        <rect x="234" y="262" width="162" height="106" fill={PARCHMENT} />
        {/* Code lines on screen */}
        <rect x="244" y="276" width="60" height="6" fill={TERRACOTTA} />
        <rect x="244" y="290" width="110" height="6" fill={INDIGO} opacity="0.8" />
        <rect x="244" y="304" width="84" height="6" fill={INDIGO} opacity="0.5" />
        <rect x="244" y="318" width="130" height="6" fill={INDIGO} opacity="0.8" />
        <rect x="244" y="332" width="70" height="6" fill={TERRACOTTA} />
        <rect x="244" y="346" width="100" height="6" fill={INDIGO} opacity="0.6" />

        {/* Notebook on right side of desk */}
        <polygon
            points="440,395 560,395 580,420 460,420"
            fill={SHADOW}
            opacity="0.35"
        />
        <rect x="440" y="360" width="120" height="40" fill={TERRACOTTA} />
        <rect x="440" y="360" width="120" height="6" fill={INDIGO} opacity="0.25" />
        <line x1="455" y1="380" x2="545" y2="380" stroke={INDIGO} strokeWidth="2" opacity="0.4" />
        <line x1="455" y1="390" x2="520" y2="390" stroke={INDIGO} strokeWidth="2" opacity="0.4" />
        {/* Pen */}
        <rect
            x="470"
            y="345"
            width="70"
            height="6"
            fill={INDIGO}
            transform="rotate(-12 470 345)"
        />
        <rect
            x="530"
            y="339"
            width="12"
            height="6"
            fill={TERRACOTTA}
            transform="rotate(-12 530 339)"
        />

        {/* Coffee cup on left */}
        <ellipse cx="150" cy="400" rx="40" ry="8" fill={SHADOW} opacity="0.3" />
        <rect x="120" y="340" width="60" height="60" fill={INDIGO} />
        <rect x="120" y="340" width="60" height="10" fill={PARCHMENT} />
        <ellipse cx="150" cy="344" rx="26" ry="4" fill={TERRACOTTA} opacity="0.6" />
        {/* Cup handle */}
        <path
            d="M180 360 Q205 370 180 390"
            fill="none"
            stroke={INDIGO}
            strokeWidth="7"
        />
        {/* Steam */}
        <path
            d="M140 320 Q146 310 140 300 Q134 290 140 280"
            fill="none"
            stroke={INDIGO}
            strokeWidth="3"
            opacity="0.5"
        />
        <path
            d="M160 320 Q166 310 160 300 Q154 290 160 280"
            fill="none"
            stroke={INDIGO}
            strokeWidth="3"
            opacity="0.35"
        />

        {/* Plant on right */}
        <rect x="495" y="335" width="55" height="45" fill={INDIGO} />
        <polygon points="495,335 550,335 540,346 505,346" fill={SHADOW} opacity="0.4" />
        <path
            d="M522 335 Q500 310 495 280 Q510 295 522 310 Q530 285 540 270 Q535 300 522 335 Z"
            fill={INDIGO}
        />
        <path
            d="M522 320 Q548 300 556 275"
            fill="none"
            stroke={TERRACOTTA}
            strokeWidth="4"
        />

        {/* Baseline */}
        <line x1="0" y1="640" x2="640" y2="640" stroke={INDIGO} strokeWidth="2" />

        {/* Tiny editorial caption */}
        <text
            x="30"
            y="610"
            fill={INDIGO}
            fontFamily="Montserrat"
            fontSize="11"
            fontWeight="700"
            letterSpacing="4"
        >
            ВНУТРИ · OPERATING SYSTEM FOR IT FOUNDERS
        </text>
    </svg>
);

/* ---------- About: reading portrait ---------- */
export const ReadingPortrait = ({ className = "" }) => (
    <svg
        viewBox="0 0 400 500"
        className={className}
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
    >
        <rect x="0" y="0" width="400" height="500" fill={CREAM} />

        {/* Sun wedge top-left */}
        <path d="M0 0 L170 0 L0 170 Z" fill={TERRACOTTA} />
        <line x1="0" y1="0" x2="170" y2="170" stroke={INDIGO} strokeWidth="2" opacity="0.3" />

        {/* Floor line */}
        <line x1="0" y1="380" x2="400" y2="380" stroke={INDIGO} strokeWidth="2" />

        {/* Chair long shadow */}
        <polygon
            points="120,380 280,380 350,460 190,460"
            fill={SHADOW}
            opacity="0.3"
        />

        {/* Figure silhouette — flat person reading */}
        {/* Chair */}
        <rect x="150" y="290" width="120" height="90" fill={INDIGO} />
        <rect x="150" y="290" width="120" height="8" fill={PARCHMENT} opacity="0.2" />
        {/* Body */}
        <path
            d="M170 290 Q170 220 210 210 Q260 200 270 260 L270 290 Z"
            fill={INDIGO}
        />
        {/* Head */}
        <circle cx="225" cy="185" r="28" fill={INDIGO} />
        {/* Hair highlight */}
        <path
            d="M200 172 Q225 155 252 170 Q250 178 225 180 Q208 178 200 172 Z"
            fill={SHADOW}
        />
        {/* Cheek highlight */}
        <circle cx="215" cy="192" r="4" fill={TERRACOTTA} opacity="0.8" />

        {/* Book in hands */}
        <polygon points="200,260 270,260 275,285 205,285" fill={TERRACOTTA} />
        <line x1="237" y1="260" x2="240" y2="285" stroke={INDIGO} strokeWidth="2" />
        {/* Arms */}
        <path d="M195 235 Q195 260 210 272" stroke={INDIGO} strokeWidth="12" strokeLinecap="round" fill="none" />
        <path d="M260 235 Q265 260 265 272" stroke={INDIGO} strokeWidth="12" strokeLinecap="round" fill="none" />

        {/* Desk lamp on right */}
        <rect x="310" y="330" width="40" height="50" fill={INDIGO} />
        <rect x="322" y="290" width="16" height="46" fill={INDIGO} />
        <polygon points="302,265 346,265 330,295 318,295" fill={TERRACOTTA} />
        <polygon
            points="320,295 328,295 340,380 300,380"
            fill={TERRACOTTA}
            opacity="0.18"
        />

        {/* Caption plate */}
        <rect x="0" y="430" width="400" height="70" fill={INDIGO} />
        <text
            x="24"
            y="460"
            fill={PARCHMENT}
            fontFamily="Montserrat"
            fontSize="14"
            fontWeight="700"
            letterSpacing="5"
        >
            ELENA KULAKOVA
        </text>
        <text
            x="24"
            y="484"
            fill={TERRACOTTA}
            fontFamily="Montserrat"
            fontSize="10"
            fontWeight="600"
            letterSpacing="3"
        >
            OPERATING · COACHING · LAUNCH
        </text>
    </svg>
);

/* ---------- Small object illustrations ---------- */
export const LaptopMini = ({ className = "" }) => (
    <svg viewBox="0 0 160 120" className={className} aria-hidden="true">
        <polygon points="20,95 140,95 150,110 30,110" fill={SHADOW} opacity="0.35" />
        <rect x="20" y="85" width="120" height="14" fill={CREAM} />
        <rect x="20" y="85" width="120" height="4" fill={INDIGO} />
        <rect x="32" y="20" width="96" height="68" fill={INDIGO} />
        <rect x="40" y="28" width="80" height="52" fill={PARCHMENT} />
        <rect x="48" y="38" width="28" height="4" fill={TERRACOTTA} />
        <rect x="48" y="48" width="50" height="4" fill={INDIGO} opacity="0.7" />
        <rect x="48" y="58" width="36" height="4" fill={INDIGO} opacity="0.5" />
        <rect x="48" y="68" width="56" height="4" fill={INDIGO} opacity="0.7" />
    </svg>
);

export const HeadphonesMini = ({ className = "" }) => (
    <svg viewBox="0 0 160 160" className={className} aria-hidden="true">
        {/* Shadow */}
        <ellipse cx="85" cy="138" rx="60" ry="6" fill={SHADOW} opacity="0.3" />
        {/* Arc */}
        <path
            d="M30 110 Q30 40 80 40 Q130 40 130 110"
            stroke={INDIGO}
            strokeWidth="10"
            fill="none"
            strokeLinecap="round"
        />
        {/* Left cup */}
        <rect x="14" y="100" width="28" height="40" rx="4" fill={INDIGO} />
        <rect x="18" y="104" width="20" height="20" fill={TERRACOTTA} />
        {/* Right cup */}
        <rect x="118" y="100" width="28" height="40" rx="4" fill={INDIGO} />
        <rect x="122" y="104" width="20" height="20" fill={TERRACOTTA} />
    </svg>
);

export const NotebookMini = ({ className = "" }) => (
    <svg viewBox="0 0 160 160" className={className} aria-hidden="true">
        <polygon points="28,132 124,132 140,146 44,146" fill={SHADOW} opacity="0.35" />
        <rect x="28" y="32" width="96" height="104" fill={TERRACOTTA} />
        <rect x="28" y="32" width="14" height="104" fill={INDIGO} opacity="0.3" />
        <line x1="52" y1="60" x2="116" y2="60" stroke={PARCHMENT} strokeWidth="3" />
        <line x1="52" y1="76" x2="108" y2="76" stroke={PARCHMENT} strokeWidth="3" />
        <line x1="52" y1="92" x2="116" y2="92" stroke={PARCHMENT} strokeWidth="3" />
        <line x1="52" y1="108" x2="92" y2="108" stroke={PARCHMENT} strokeWidth="3" />
        {/* Pen */}
        <rect
            x="70"
            y="16"
            width="70"
            height="8"
            fill={INDIGO}
            transform="rotate(20 70 16)"
        />
    </svg>
);

export const CoffeeMini = ({ className = "" }) => (
    <svg viewBox="0 0 160 160" className={className} aria-hidden="true">
        <ellipse cx="80" cy="140" rx="54" ry="8" fill={SHADOW} opacity="0.3" />
        <rect x="40" y="60" width="72" height="78" fill={INDIGO} />
        <rect x="40" y="60" width="72" height="10" fill={PARCHMENT} />
        <ellipse cx="76" cy="66" rx="30" ry="4" fill={TERRACOTTA} opacity="0.7" />
        <path
            d="M112 80 Q140 90 112 120"
            stroke={INDIGO}
            strokeWidth="9"
            fill="none"
        />
        <path
            d="M60 40 Q66 30 60 20 Q54 10 60 0"
            stroke={INDIGO}
            strokeWidth="3"
            fill="none"
            opacity="0.5"
        />
        <path
            d="M84 40 Q90 30 84 20 Q78 10 84 0"
            stroke={INDIGO}
            strokeWidth="3"
            fill="none"
            opacity="0.35"
        />
    </svg>
);

export const CompassMini = ({ className = "" }) => (
    <svg viewBox="0 0 160 160" className={className} aria-hidden="true">
        <circle cx="80" cy="80" r="58" fill={INDIGO} />
        <circle cx="80" cy="80" r="48" fill={PARCHMENT} />
        <polygon points="80,36 92,80 80,124 68,80" fill={TERRACOTTA} />
        <circle cx="80" cy="80" r="6" fill={INDIGO} />
        <text
            x="80"
            y="32"
            fill={INDIGO}
            fontFamily="Montserrat"
            fontSize="10"
            fontWeight="700"
            textAnchor="middle"
        >
            N
        </text>
    </svg>
);

export const SunBurst = ({ className = "" }) => (
    <svg viewBox="0 0 200 200" className={className} aria-hidden="true">
        <g className="vnutri-sun-rotate" style={{ transformOrigin: "100px 100px" }}>
            <circle cx="100" cy="100" r="40" fill={TERRACOTTA} />
            {Array.from({ length: 16 }).map((_, i) => {
                const a = (i * Math.PI) / 8;
                const x1 = 100 + Math.cos(a) * 52;
                const y1 = 100 + Math.sin(a) * 52;
                const x2 = 100 + Math.cos(a) * 72;
                const y2 = 100 + Math.sin(a) * 72;
                return (
                    <line
                        key={i}
                        x1={x1}
                        y1={y1}
                        x2={x2}
                        y2={y2}
                        stroke={TERRACOTTA}
                        strokeWidth="5"
                        strokeLinecap="round"
                    />
                );
            })}
        </g>
    </svg>
);
